import { Component, OnInit, inject } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import {
    ParentChildSelectorConfig,
    ParentChildButtonConfig,
} from "@app/shared/models/ParentChildSelector/ParentChildSelectorConfig";
import { ControlCopyEffectivenessDataService } from "./control-copy-effectiveness.data.service";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ControlExportData } from "@app/controls/models/ControlExportData";
import { AdvancedEffectivenessExportData } from "@app/controls/models/AdvancedEffectivenessExportData";
import { nameof } from "@methods/jeffs-toolkit";
import { ControlCopyExportSharedMethods } from "@app/controls/control-copy-export-shared-methods";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { CerrixParentChildSelectorComponent } from "@app/shared/cerrix-parent-child-selector/cerrix-pcs.component";

@Component({
    selector: "control-copy-effectiveness",
    templateUrl: "./control-copy-effectiveness.component.html",
    styleUrls: ["./control-copy-effectiveness.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [CerrixLoaderComponent, CerrixParentChildSelectorComponent],
})
export class ControlCopyEffectivenessComponent implements OnInit {
    private _ds = inject(ControlCopyEffectivenessDataService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    tab: TabModel;
    config: string[];

    selectorConfig: ParentChildSelectorConfig;

    async ngOnInit(): Promise<void> {
        await this._translation.ready();

        const buttonConfig: ParentChildButtonConfig[] = [
            {
                prettyName: this._translation.t("copyEffectiveness.copyTestplan"),
                icon: "fas fa-file-archive",
                disabled: false,
                click: this.handleClick.bind(this),
            },
        ];

        this._ds.getWithGuids(this.config).subscribe((controls) => {
            controls.forEach((control) => {
                control.header = `${control.identifier} - ${control.name}`;
                control.effectivenessList = control.effectivenessList.sortBy("enddate", true);
                control.effectivenessList.forEach((effectiveness) => {
                    if (effectiveness.testPeriodName) {
                        effectiveness.renderedName = `${effectiveness.testPeriodName} - ${effectiveness.name}`;
                    } else {
                        effectiveness.renderedName = effectiveness.name;
                    }
                });
            });

            this.selectorConfig = ControlCopyExportSharedMethods.createSelectorConfig(
                controls,
                buttonConfig,
                this._translation
            );

            this.selectorConfig.filters.push({
                prettyName: this._translation.t(
                    "copyEffectiveness.testPeriodFilter"
                ),
                propertyName: nameof<AdvancedEffectivenessExportData>((eff) => eff.testPeriodName),
                filterType: GenericListFieldType.MultiSelect,
                childFilter: true,

                data: controls
                    .selectMany<ControlExportData, string>((c) =>
                        c.effectivenessList.map((e) => e.testPeriodName)
                    )
                    .distinct()
                    .sort(),
            });

            this.tab.showLoader = false;
        });
    }

    private async handleClick(allItems: ControlExportData[]): Promise<void> {
        {
            const items = this.getSelectedExportData(allItems);
            if (items.length <= 0) {
                this._promptService.alert({
                    data: {
                        title: this._translation.t("copyEffectiveness.selectionRequiredTitle"),
                        message: this._translation.t("copyEffectiveness.selectionRequiredMessage"),
                    },
                });
                return;
            }

            const newGroupName = (
                await this._promptService
                    .prompt({
                        maxHeight: "250px",
                        maxWidth: "440px",
                        data: {
                            title: this._translation.t("copyEffectiveness.newGroupNameTitle"),
                            fields: [
                                {
                                    prettyName: this._translation.t("copyEffectiveness.groupNameFieldLabel"),
                                    fieldName: "groupname",
                                    fieldType: GenericListFieldType.Text,
                                },
                            ],
                        },
                    })
                    .toPromise()
            ).groupname;

            if (newGroupName == null) return;

            const count = items
                .map((control) => {
                    const selectedEffectiveness = control.effectivenessList.filter(
                        (effectiveness) => {
                            return effectiveness.selected === true;
                        }
                    );
                    return selectedEffectiveness.length;
                })
                .reduce(function (total, num) {
                    return total + num;
                });

            const confirmText = this._translation.t("copyEffectiveness.confirmCopyMessage", { count });

            if (await this._promptService.confirm(this._translation.t("copyEffectiveness.confirmCopyTitle"), confirmText).toPromise()) {
                this.tab.loadingMessage = this._translation.t("copyEffectiveness.copyingLoadingMessage");
                this.tab.showLoader = true;
                this._ds.copyData(items, newGroupName).then(() => {
                    this._toastr.success(
                        "",
                        this._translation.t("copyEffectiveness.copySuccessMessage", { count })
                    );

                    this.tab.loadingMessage = null;
                    this.tab.refresh();
                });
            }
        }
    }

    private getSelectedExportData(data: ControlExportData[]): ControlExportData[] {
        const selectedItems: ControlExportData[] = [];

        const copyData: ControlExportData[] = JSON.parse(JSON.stringify(data));
        copyData.forEach((control) => {
            control.effectivenessList = control.effectivenessList.filter((eff) => eff.selected);
            if (control.effectivenessList.length > 0) {
                selectedItems.push(control);
            }
        });

        return selectedItems;
    }
}
